// 🔧 agent_runs.js
// ✅ Purpose: Admin-facing AI Agent Runs history page for Let's Go Vote Sales Automation
// ✅ Features:
// - Displays standardized AI agent execution logs from Firestore `agent_runs`
// - Supports filtering by agent name
// - Supports filtering by run status
// - Shows agent summary, metrics, duration, and error messages
// - Links back to AI Mission Control and Sales Dashboard
// - Does NOT run agents or contact prospects automatically
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import SalesPageLayout from "../../components/sales/SalesPageLayout";

function AgentRuns() {
  const [agentRuns, setAgentRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  const [agentFilter, setAgentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const showStatus = (type, message) => {
    setStatus({ type, message });
    setTimeout(() => setStatus(null), 20000);
  };

  const loadAgentRuns = async () => {
    try {
      setLoading(true);

      console.log("🤖 Loading AI agent runs...");

      const runsSnap = await getDocs(
        query(collection(db, "agent_runs"), orderBy("createdAt", "desc"))
      );

      const loadedRuns = runsSnap.docs.map((runDoc) => ({
        id: runDoc.id,
        ...runDoc.data(),
      }));

      console.log("✅ Agent runs loaded:", loadedRuns.length);

      setAgentRuns(loadedRuns);
    } catch (error) {
      console.error("❌ Failed to load agent runs:", error);
      showStatus("error", "Failed to load agent runs. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgentRuns();
  }, []);

  const formatTimestamp = (value) => {
    if (!value) return "Not recorded";

    const date = value?.toDate ? value.toDate() : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "Not recorded";
    }

    return date.toLocaleString();
  };

  const getDurationLabel = (run) => {
    const durationMs = Number(run.metrics?.durationMs || 0);

    if (!durationMs) {
      return "Not recorded";
    }

    const seconds = Math.round(durationMs / 1000);

    if (seconds < 60) {
      return `${seconds}s`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes}m ${remainingSeconds}s`;
  };

  const getStatusBadgeClass = (runStatus) => {
    if (runStatus === "completed") return "bg-success";
    if (runStatus === "failed") return "bg-danger";
    if (runStatus === "running") return "bg-warning text-dark";

    return "bg-secondary";
  };

  const agentOptions = useMemo(() => {
    const uniqueAgents = Array.from(
      new Set(agentRuns.map((run) => run.agentName).filter(Boolean))
    );

    return uniqueAgents.sort();
  }, [agentRuns]);

  const filteredRuns = useMemo(() => {
    return agentRuns.filter((run) => {
      const matchesAgent =
        agentFilter === "all" || run.agentName === agentFilter;

      const matchesStatus =
        statusFilter === "all" || run.status === statusFilter;

      return matchesAgent && matchesStatus;
    });
  }, [agentRuns, agentFilter, statusFilter]);

  return (
    <SalesPageLayout
      title="Agent Runs"
      subtitle="Execution history and audit trail for LGV Sales Automation agents."
      status={status}
      actions={
        <>
          <Link className="btn btn-outline-primary" to="/admin/ai-mission-control">
            AI Mission Control
          </Link>

          <Link className="btn btn-outline-secondary" to="/admin/sales-dashboard">
            Sales Dashboard
          </Link>

          <button
            type="button"
            className="btn btn-outline-dark"
            onClick={loadAgentRuns}
          >
            Refresh
          </button>
        </>
      }
    >
      <div className="card mb-3">
        <div className="card-header">
          <strong>Filters</strong>
        </div>

        <div className="card-body">
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label">Agent</label>
              <select
                className="form-select"
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
              >
                <option value="all">All Agents</option>

                {agentOptions.map((agentName) => (
                  <option key={agentName} value={agentName}>
                    {agentName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="running">Running</option>
              </select>
            </div>

            <div className="col-md-4 mb-3 d-flex align-items-end">
              <div className="text-muted">
                Showing {filteredRuns.length} of {agentRuns.length} run(s).
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="alert alert-info">Loading agent runs...</div>
      ) : filteredRuns.length === 0 ? (
        <div className="alert alert-info">
          No agent runs found for the selected filters.
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <strong>Agent Run History</strong>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-sm table-bordered align-middle mb-0">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>Status</th>
                    <th>Started</th>
                    <th>Completed</th>
                    <th>Duration</th>
                    <th>Summary</th>
                    <th>Metrics</th>
                    <th>Error</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredRuns.map((run) => (
                    <tr key={run.id}>
                      <td>
                        <strong>{run.agentName || "unknown_agent"}</strong>

                        <div className="small text-muted">
                          Initiated by: {run.initiatedBy || "manual"}
                        </div>
                      </td>

                      <td>
                        <span className={`badge ${getStatusBadgeClass(run.status)}`}>
                          {run.status || "unknown"}
                        </span>
                      </td>

                      <td>{formatTimestamp(run.startedAt)}</td>

                      <td>{formatTimestamp(run.completedAt)}</td>

                      <td>{getDurationLabel(run)}</td>

                      <td>{run.summary || ""}</td>

                      <td>
                        <pre
                          className="small mb-0"
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          {JSON.stringify(run.metrics || {}, null, 2)}
                        </pre>
                      </td>

                      <td>
                        {run.errorMessage ? (
                          <span className="text-danger small">
                            {run.errorMessage}
                          </span>
                        ) : (
                          <span className="text-muted small">None</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </SalesPageLayout>
  );
}

export default AgentRuns;